import { getReferenceId } from '@quromedical/fhir-common';
import {
  type CareTeam,
  type CareTeamMembersSchema,
  CareTeamValidationSchema,
  type UpdateCareTeam,
} from '@quromedical/models';
import { first } from '@quromedical/utils';
import { CrudForm } from 'components/form';
import type { OrganizationFetcher } from 'core/group';
import { Card, Skeleton, SkeletonProvider, Visible } from 'design-system';
import { useFiniteState } from 'hooks/useFiniteState';
import { useList } from 'hooks/useList';
import { useRevalidation } from 'hooks/useRevalidation';
import { CareTeamApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { strings } from 'strings';

import { fields } from './fields';
import { GeneralForm } from './form';
import { GeneralCareTeamCard } from './GeneralCareTeamCard';

export type UpdateBaseCareTeam = Omit<
  UpdateCareTeam,
  'period' | 'teamMembers' | 'managingOrganization'
> & {
  managingOrganization: string;
  start: string;
};

export interface CareTeamBaseTemplate extends UpdateBaseCareTeam {
  teamMembers: CareTeamMembersSchema[];
}

export interface InitialCareTeamTemplate
  extends Omit<Partial<CareTeamBaseTemplate>, 'teamMembers'> {
  teamMembers: Partial<CareTeamMembersSchema>[];
}

export interface CareTeamTemplateFormProps {
  initial?: InitialCareTeamTemplate;
  organizationFetcher: OrganizationFetcher;
  onSubmit: (data: UpdateCareTeam) => Promise<void>;
  onCancel?: () => void;
  hideCard?: boolean;
  canEdit: boolean;
}

interface ListDataValue {
  isValid: boolean;
  hasSubmitted: boolean;
  data: Partial<CareTeamMembersSchema>;
}

interface ListData {
  key: number | string;
  value: ListDataValue;
}

export const InitialCareTeam = (
  careTeam?: CareTeam,
): InitialCareTeamTemplate => {
  return {
    id: careTeam?.id,
    name: careTeam?.name,
    status: careTeam?.status,
    start: careTeam?.period.start,
    managingOrganization: getReferenceId(first(careTeam?.managingOrganization)),
    teamMembers:
      careTeam?.teamMembers?.map((t) => ({
        role: first(t.role)?.text,
        member: getReferenceId(t.member),
      })) || [],
  };
};

export const createEmptyItem = (): ListData => ({
  key: Math.random(),
  value: {
    hasSubmitted: false,
    isValid: false,
    data: {},
  },
});

export type ManagedOutsideOfForm = undefined;

export interface UpdateCareTeamTemplate
  extends Omit<CareTeamBaseTemplate, 'teamMembers'> {
  teamMembers: ManagedOutsideOfForm;
}

/**
 * Assumes that the input data is valid
 */
export const getInitialCareTeamLines = (
  careTeamLines: Partial<CareTeamMembersSchema>[] = [],
): ListData[] => {
  if (!careTeamLines.length) {
    return [createEmptyItem()];
  }

  return careTeamLines.map((line) => ({
    key: line.member || '',
    value: {
      data: line,
      hasSubmitted: true,
      isValid: true,
    },
  }));
};

type State =
  | 'initial'
  | 'editing'
  | 'submitting'
  | 'item-error'
  | 'submission-error';

type ValidListDataValue = ListDataValue & { data: CareTeamMembersSchema };

export const areItemsValid = (
  items: Partial<ListDataValue>[],
): items is ValidListDataValue[] => items.every((item) => item.isValid);

export const CareTeamForm: React.FC<CareTeamTemplateFormProps> = ({
  initial,
  organizationFetcher,
  hideCard = false,
  canEdit,
}) => {
  const state = useFiniteState<State>('initial');
  const revalidate = useRevalidation(`${initial?.id}`);

  const initialLines = useMemo(
    () => getInitialCareTeamLines(initial?.teamMembers),
    [initial],
  );
  const lines = useList(initialLines);

  const items = lines.items.map((i) => i.value);
  const itemsValid = areItemsValid(items);

  const handleSubmit = useCallback(
    async ({
      id,
      name,
      status,
      managingOrganization,
      start,
    }: UpdateCareTeamTemplate) => {
      if (!itemsValid) {
        state.set('item-error');
        return {};
      }

      state.set('submitting');
      try {
        const api = new CareTeamApi();

        await api.updateCareTeam({
          id,
          status: status,
          name,
          period: {
            start,
          },
          managingOrganization: [managingOrganization],
          teamMembers: items.map((t) => ({
            role: [t.data.role],
            member: t.data.member,
          })),
        });

        await revalidate();
        return {};
      } catch (err) {
        state.set('submission-error');
        return { error: err };
      }
    },
    [items, itemsValid, revalidate, state],
  );

  const hasError = state.is('submission-error') || state.is('item-error');
  const errorMessage = state.is('submission-error')
    ? strings.ClaimTemplateFormSubmissionError
    : strings.ClaimTemplateFormItemsError;

  const error = hasError ? errorMessage : undefined;
  const fieldsData = fields(organizationFetcher);

  return (
    <>
      <SkeletonProvider loading={state.is('submitting')}>
        <Skeleton>
          <Card>
            <Visible if={state.isNot('editing')}>
              <GeneralCareTeamCard
                data={initial}
                canEdit={canEdit}
                onEditPress={state.next('editing')}
              />
            </Visible>
            <Visible if={state.is('editing')}>
              <CrudForm<UpdateCareTeamTemplate>
                hasCard={!hideCard}
                fields={fieldsData}
                initialValues={{ ...initial, teamMembers: undefined }}
                validationSchema={CareTeamValidationSchema}
                error={error}
                showErrors={hasError}
                onSubmit={handleSubmit}
                secondaryButtonText={strings.ButtonTextCancel}
                onSecondarySubmit={state.next('initial')}
                showSecondarySubmitButton={true}
              >
                {lines.items.map((item, index) => {
                  const isLast = index === lines.items.length - 1;
                  const canRemove = lines.items.length > 1;

                  return (
                    <GeneralForm
                      key={item.key}
                      organizationFetcher={organizationFetcher}
                      onChange={(data, isValid) => {
                        lines.update(item.key, {
                          ...item.value,
                          data,
                          isValid,
                        });
                      }}
                      onRemove={
                        canRemove ? () => lines.remove(item.key) : undefined
                      }
                      onAdd={
                        isLast
                          ? () => {
                              const emptyItem = createEmptyItem();
                              lines.update(item.key, {
                                ...item.value,
                                hasSubmitted: true,
                              });
                              lines.add(emptyItem.key, emptyItem.value);
                            }
                          : undefined
                      }
                      data={item.value.data}
                      showErrors={item.value.hasSubmitted || hasError}
                    />
                  );
                })}
              </CrudForm>
            </Visible>
          </Card>
        </Skeleton>
      </SkeletonProvider>
    </>
  );
};
